import clsx from 'clsx'
import type { KeyboardEvent, MouseEvent, PropsWithChildren } from 'react'
import React from 'react'
import { useIntl } from 'react-intl'
import type {
  PaginationColor,
  PaginationSize,
  PaginationVariant,
} from './Pagination'
import { sizes, variants } from './Pagination'

interface PageItemProps {
  /**
   * The color scheme of the pagination component
   */
  color: PaginationColor
  /**
   * If true, the page item is disabled
   */
  disabled: boolean
  /**
   * The page number for this item
   */
  index: number
  /**
   * The current selected page number.
   * Used to determine if the current page is selected
   */
  page: number
  /**
   * Callback fired when the page item is activated
   */
  onClick: (event: MouseEvent | KeyboardEvent) => void
  /**
   * The size of the page component
   */
  size: PaginationSize
  /**
   * The visual style variant of the page component
   */
  variant: PaginationVariant
}

function PageItem({
  color,
  disabled,
  index,
  page,
  onClick,
  size,
  variant,
}: PropsWithChildren<PageItemProps>): JSX.Element {
  const intl = useIntl()

  return (
    <li>
      <button
        aria-current={page === index}
        aria-label={intl.formatMessage(
          { id: 'Pagination.go_to_page_number' },
          { index },
        )}
        className={clsx(
          'text-black-60 text-sm font-normal rounded-full leading-relaxed disabled:cursor-not-allowed disabled:text-black-25 focus:outline-none',
          sizes[size],
          page === index
            ? variants[variant][color].selected
            : disabled
            ? ''
            : variants[variant][color].unselected,
        )}
        disabled={disabled}
        onClick={onClick}
        onKeyPress={onClick}
        type="button"
        value={index}
      >
        {index}
      </button>
    </li>
  )
}

export default PageItem
